import { Collapse, Divider } from 'antd';
import classNames from 'classnames';
import React, { FC, memo, useCallback, useMemo, useState } from 'react';
import { useGetCurrencySymbol } from 'app/appState';
import { User, getUserName, useRole } from 'entities/User';
import { ReactComponent as CollapseArrow } from 'shared/assets/icons/CollapseArrow.svg';
import { BulletsTable, TableRow } from 'shared/ui/BulletsTable';
import { Button } from 'shared/ui/Button';
import { getClientDateFormat } from 'shared/utils/helpers/getDateFormat';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { roundNumber } from 'shared/utils/helpers/roundNumber';
import { Payment, PaymentStatus, generatePaymentPdfLink } from 'entities/Payment';
import { MarkAsRefundedPayment } from 'features/MarkAsRefundedPayment';
import { CreditAndRefundPayment } from 'features/CreditAndRefundPayment';
import { CreditPayment } from 'features/CreditPayment';
import s from './PaymentCard.module.scss';
import { Deposit } from 'entities/Deposit';
import { splitDocumentDescription } from 'shared/utils/helpers/splitDocumentDescription';
import { PaymentCreditNoteType } from 'entities/CreditNote/model/types';
import { CreditAndRefundDetailsModal } from './CreditAndRefundDetailsModal';
import { InvoiceItemType } from 'entities/Invoice';

interface PaymentCardProps {
  user: User;
  payment: Payment;
  deposit?: Deposit;
  isClosedContract: boolean;
  isDepositPayment?: boolean;
}

export const PaymentCard: FC<PaymentCardProps> = memo((props) => {
  const { payment, user, isDepositPayment, deposit, isClosedContract } = props;

  const { documentNumber: invoiceNumber, invoiceEmail } = payment.invoice;

  const showIsDepositPayment = useMemo(() => Boolean(isDepositPayment && deposit && !deposit.isRefunded), [deposit, isDepositPayment]);

  const { t } = useAppTranslation(['contracts', 'common']);

  const { isManagement } = useRole();

  const currencySymbol = useGetCurrencySymbol();

  const [isCreditNoteModalOpen, setIsCreditNoteModalOpen] = useState(false);

  const isPaymentCompleted = payment.paymentStatus === PaymentStatus.COMPLETED;

  const isPaymentReversalCredit = payment.creditNote?.creditNoteType === PaymentCreditNoteType.PAYMENT_REVERSAL_CREDIT;
  const canUseRefundActions = Boolean(isClosedContract || (payment.creditNote && !isPaymentReversalCredit));

  const creditedAmount = payment.creditNote?.creditedAmount;
  const refundedAmount = payment.refund?.amount;

  const showCreditAndRefundDetails = isManagement && payment.creditNote?.creditNoteItems && payment.creditNote.creditNoteItems.length > 0;

  const activePaymentAmount = useMemo(() => {
    const amount = payment.creditNote ? payment.amount - payment.creditNote.creditedAmount : payment.amount;

    return roundNumber(amount);
  }, [payment.amount, payment.creditNote]);

  const openCreditNoteModal = useCallback(() => {
    setIsCreditNoteModalOpen(true);
  }, []);

  const closeCreditNoteModal = useCallback(() => {
    setIsCreditNoteModalOpen(false);
  }, []);

  const PaymentContent = useCallback((): JSX.Element => {
    const mainRows: TableRow[] = [
      {
        label: t('Payment method'),
        value: t(payment.paymentMethod, { ns: 'common' }),
      },
      {
        label: t('Payment'),
        value: `${roundNumber(payment.amount)} ${currencySymbol}`,
        emptyValueSymbol: `0 ${currencySymbol}`,
      },
      {
        label: t('Incl. Vat'),
        value: `${roundNumber(payment.vatRateAmount)} ${currencySymbol}`,
        emptyValueSymbol: `0 ${currencySymbol}`,
      },
    ];

    if (isManagement) {
      const splitedDescription = splitDocumentDescription(payment.description);

      mainRows.push({
        label: t('Description'),
        value: `${t(splitedDescription.description, { ns: 'common' })} ${splitedDescription.documentNumber}`,
        emptyValueSymbol: '-',
      });

      if (payment.markedAsPaidBy) {
        mainRows.push({
          label: t('Marked as paid by'),
          value: getUserName(payment.markedAsPaidBy) || null,
          emptyValueSymbol: '-',
        });
      }
    }

    const creditNoteRows: TableRow[] = [];

    if (payment.creditNote) {
      creditNoteRows.push({
        label: t('Cancelled amount'),

        value: `-${roundNumber(payment.creditNote.creditedAmount)} ${currencySymbol}`,
        emptyValueSymbol: `0 ${currencySymbol}`,
      });

      creditNoteRows.push({
        label: t('Date'),
        value: `${getClientDateFormat(payment.creditNote.createdAt)}`,
        emptyValueSymbol: '-',
      });

      if (isManagement) {
        const splitedDescription = splitDocumentDescription(payment.creditNote.description);

        creditNoteRows.push({
          label: t('Description'),
          value: `${t(splitedDescription.description, { ns: 'common' })} ${splitedDescription.documentNumber}`,
          emptyValueSymbol: '-',
        });

        creditNoteRows.push({
          label: t('Cancelled by'),
          value: getUserName(payment.creditNote.issuedBy) || null,
          emptyValueSymbol: '-',
        });
      }
    }

    const refundRows: TableRow[] = [];

    if (payment.refund) {
      refundRows.push({
        label: t('Refund method'),
        value: t(payment.refund.paymentMethod || '-', { ns: 'common' }),
      });

      refundRows.push({
        label: t('Refund amount'),
        value: `${roundNumber(payment.refund.amount)} ${currencySymbol}`,
        emptyValueSymbol: `0 ${currencySymbol}`,
      });

      refundRows.push({
        label: t('Date'),
        value: `${getClientDateFormat(payment.refund.createdAt)}`,
        emptyValueSymbol: '-',
      });

      if (isManagement) {
        if (payment.refund.description) {
          const splitedDescription = splitDocumentDescription(payment.refund.description);

          refundRows.push({
            label: t('Description'),
            value: `${t(splitedDescription.description, { ns: 'common' })} ${splitedDescription.documentNumber}`,
            emptyValueSymbol: '-',
          });
        }

        refundRows.push({
          label: t('Refunded by'),
          value: getUserName(payment.refundedBy) || null,
          emptyValueSymbol: '-',
        });
      }
    }

    return (
      <div>
        <div>
          {t('From')}: {getUserName(user)}
        </div>
        <div>
          {t('Mail')}: {invoiceEmail}
        </div>
        {showCreditAndRefundDetails && (
          <div className="my-3">
            <span onClick={openCreditNoteModal} className="text-sm font-medium text-accent cursor-pointer hover:underline">
              {t('Show credit and refund details')}
            </span>
          </div>
        )}
        <Divider className="border-primary" />

        <BulletsTable theme="clear" rows={mainRows} />

        {isManagement && payment.paymentItems && payment.paymentItems.length > 0 && (
          <>
            <Divider className="border-primary" />
            <div>
              {payment.paymentItems.map((item) => {
                const isRentalItem = item.paymentItemType === InvoiceItemType.RENTAL_ITEM;

                const paymentItemType = isRentalItem
                  ? item.invoiceItem?.contractItem?.rentalItem?.rentalItemType || item.paymentItemType
                  : item.paymentItemType;

                const rows = [
                  {
                    label: t('Amount'),
                    value: `${roundNumber(item.amount)} ${currencySymbol}`,
                    emptyValueSymbol: `0 ${currencySymbol}`,
                  },
                  {
                    label: t('VAT'),
                    value: `${item.vatRatePercent}%`,
                    emptyValueSymbol: '0%',
                  },
                  {
                    label: t('VAT amount'),
                    value: `${roundNumber(item.vatRateAmount)} ${currencySymbol}`,
                    emptyValueSymbol: `0 ${currencySymbol}`,
                  },
                ];

                if (item.description) {
                  const splitedDescription = splitDocumentDescription(item.description);

                  rows.push({
                    label: t('Description'),
                    value: `${t(splitedDescription.description, { ns: 'common' })} ${splitedDescription.documentNumber}`,
                    emptyValueSymbol: '-',
                  });
                }

                return (
                  <div key={item.paymentItemId}>
                    {paymentItemType && <div className="mt-3">{t(paymentItemType)}</div>}
                    <BulletsTable theme="clear" rows={rows} />
                  </div>
                );
              })}
            </div>
          </>
        )}

        {creditNoteRows.length > 0 && (
          <>
            <Divider className="border-primary" />
            <BulletsTable theme="clear" rows={creditNoteRows} />
          </>
        )}

        {refundRows.length > 0 && (
          <>
            <Divider className="border-primary" />
            <BulletsTable theme="clear" rows={refundRows} />
          </>
        )}

        <Divider className="border-primary" />
        <BulletsTable
          theme="clear"
          rows={[
            {
              label: t('Total'),
              value: `${activePaymentAmount} ${currencySymbol}`,
              emptyValueSymbol: `0 ${currencySymbol}`,
              highlighted: true,
            },
          ]}
        />

        {!payment.creditNote && (
          <div className="my-3">
            <a
              href={generatePaymentPdfLink(payment.paymentId)}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm font-medium text-accent"
            >
              {t('Download PDF')}
            </a>
          </div>
        )}

        {isManagement && !payment.refund && !isPaymentReversalCredit && (
          <div className="flex flex-col gap-3 mt-6">
            {canUseRefundActions && (
              <MarkAsRefundedPayment
                payment={payment}
                showIsDepositPayment={showIsDepositPayment}
                isClosedContract={isClosedContract}
                deposit={deposit}
                action={<Button theme="secondary">{t('Mark as Refunded')}</Button>}
              />
            )}
            {!payment.creditNote && (
              <CreditPayment
                paymentId={payment.paymentId}
                amount={payment.amount}
                isClosedContract={isClosedContract}
                action={<Button theme="danger">{t('Cancel Payment')}</Button>}
              />
            )}
            {canUseRefundActions && payment.transactionId && (
              <CreditAndRefundPayment
                payment={payment}
                isClosedContract={isClosedContract}
                showIsDepositPayment={showIsDepositPayment}
                deposit={deposit}
                action={<Button theme="dark">{t('Refund')}</Button>}
              />
            )}
          </div>
        )}
      </div>
    );
  }, [
    activePaymentAmount,
    currencySymbol,
    deposit,
    invoiceEmail,
    isClosedContract,
    canUseRefundActions,
    isPaymentReversalCredit,
    isManagement,
    openCreditNoteModal,
    payment,
    showCreditAndRefundDetails,
    showIsDepositPayment,
    t,
    user,
  ]);

  return (
    <>
      <CreditAndRefundDetailsModal payment={payment} deposit={deposit} isOpen={isCreditNoteModalOpen} onClose={closeCreditNoteModal} />

      <Collapse
        className={classNames(s.collapse, {
          [s.credited]: creditedAmount && creditedAmount === payment.amount,
          [s.refunded]: refundedAmount && refundedAmount === payment.amount,
          [s.paymentAndCreditNote]: creditedAmount && !refundedAmount && creditedAmount !== payment.amount,
          [s.paymentAndRefund]: refundedAmount && refundedAmount !== payment.amount,
          [s.completed]: isPaymentCompleted && !payment.refund && !payment.creditNote,
        })}
        items={[
          {
            key: payment.paymentId,
            label: (
              <>
                <div className="flex justify-between font-semibold text-lg">
                  <div>
                    <span className="font-semibold text-lg">{payment.documentNumber}</span>
                    {payment.paymentStatus !== PaymentStatus.COMPLETED && <span className="text-m">({t(payment.paymentStatus)})</span>}
                  </div>
                  <div className="text-accent">{invoiceNumber}</div>
                </div>
                <div>{getClientDateFormat(payment.createdAt)}</div>
              </>
            ),
            children: <PaymentContent />,
          },
        ]}
        expandIcon={(panelProps) => (
          <CollapseArrow
            className={classNames('ease-linear duration-200', {
              '-rotate-90 fill-primaryLight': panelProps.isActive,
              'rotate-90 fill-secondaryAccent': !panelProps.isActive,
            })}
          />
        )}
        expandIconPosition="end"
      />
    </>
  );
});
